import { useMemo, useCallback, useRef, useEffect } from "react";

/**
 * Hook personalizado para optimizaciones de rendimiento en el módulo dental
 * Proporciona memoización inteligente, debouncing y cache para operaciones frecuentes
 */
export default function useDentalPerformance() {
  const cacheRef = useRef(new Map());
  const timersRef = useRef(new Map());

  // Limpiar timers al desmontar
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  // Cache con TTL (Time To Live)
  const setCache = useCallback((key, value, ttl = 300000) => {
    // 5 minutos default
    const expiresAt = Date.now() + ttl;
    cacheRef.current.set(key, { value, expiresAt });
  }, []);

  const getCache = useCallback((key) => {
    const cached = cacheRef.current.get(key);
    if (!cached) return null;

    if (Date.now() > cached.expiresAt) {
      cacheRef.current.delete(key);
      return null;
    }

    return cached.value;
  }, []);

  const clearCache = useCallback((pattern) => {
    if (pattern) {
      const keys = Array.from(cacheRef.current.keys());
      keys.forEach((key) => {
        if (key.includes(pattern)) {
          cacheRef.current.delete(key);
        }
      });
    } else {
      cacheRef.current.clear();
    }
  }, []);

  // Debouncing optimizado
  const debounce = useCallback((func, delay, key) => {
    return (...args) => {
      const existingTimer = timersRef.current.get(key);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      const timer = setTimeout(() => {
        func(...args);
        timersRef.current.delete(key);
      }, delay);

      timersRef.current.set(key, timer);
    };
  }, []);

  // Throttling para eventos frecuentes
  const throttle = useCallback((func, limit, key) => {
    let inThrottle;
    return (...args) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }, []);

  // Memoización de cálculos costosos
  const memoizeExpensive = useCallback(
    (fn, keyGen, ttl) => {
      return (...args) => {
        const key = keyGen ? keyGen(...args) : JSON.stringify(args);
        const cached = getCache(`memo_${key}`);

        if (cached !== null) {
          return cached;
        }

        const result = fn(...args);
        setCache(`memo_${key}`, result, ttl);
        return result;
      };
    },
    [getCache, setCache]
  );

  // Optimizaciones específicas para datos dentales
  const optimizeDentalData = useMemo(
    () => ({
      // Agrupar dientes por estado para rendering eficiente
      groupToothByStatus: memoizeExpensive(
        (dentalData) => {
          const grouped = {};
          Object.entries(dentalData).forEach(([tooth, status]) => {
            if (!grouped[status]) grouped[status] = [];
            grouped[status].push(tooth);
          });
          return grouped;
        },
        (dentalData) => `group_${JSON.stringify(dentalData)}`,
        600000 // 10 minutos
      ),

      // Calcular estadísticas de salud dental
      calculateDentalStats: memoizeExpensive(
        (dentalData) => {
          const totalTeeth = Object.keys(dentalData).length;
          const statusCounts = {};

          Object.values(dentalData).forEach((status) => {
            statusCounts[status] = (statusCounts[status] || 0) + 1;
          });

          const healthyCount = statusCounts.healthy || 0;
          const problemsCount = totalTeeth - healthyCount;
          const healthPercentage =
            totalTeeth > 0 ? (healthyCount / totalTeeth) * 100 : 0;

          return {
            totalTeeth,
            healthyCount,
            problemsCount,
            healthPercentage: Math.round(healthPercentage * 100) / 100,
            statusBreakdown: statusCounts,
          };
        },
        (dentalData) => `stats_${JSON.stringify(dentalData)}`,
        300000 // 5 minutos
      ),

      // Validar estructura de datos dental
      validateDentalStructure: memoizeExpensive(
        (dentalData) => {
          const validStatuses = [
            "healthy",
            "caries",
            "filled",
            "extracted",
            "prosthesis",
            "crown",
            "implant",
          ];
          const validTeeth = [];
          const invalidTeeth = [];
          const unknownStatuses = new Set();

          Object.entries(dentalData).forEach(([tooth, status]) => {
            if (validStatuses.includes(status)) {
              validTeeth.push(tooth);
            } else {
              invalidTeeth.push(tooth);
              unknownStatuses.add(status);
            }
          });

          return {
            isValid: invalidTeeth.length === 0,
            validTeeth,
            invalidTeeth,
            unknownStatuses: Array.from(unknownStatuses),
            validationScore: validTeeth.length / Object.keys(dentalData).length,
          };
        },
        (dentalData) => `validate_${JSON.stringify(dentalData)}`,
        900000 // 15 minutos
      ),
    }),
    [memoizeExpensive]
  );

  // Utilidades de performance
  const performanceUtils = useMemo(
    () => ({
      // Marcar inicio de operación
      startOperation: (operationName) => {
        if (typeof performance !== "undefined" && performance.mark) {
          performance.mark(`dental_${operationName}_start`);
        }
      },

      // Marcar fin de operación y medir
      endOperation: (operationName) => {
        if (
          typeof performance !== "undefined" &&
          performance.mark &&
          performance.measure
        ) {
          performance.mark(`dental_${operationName}_end`);
          performance.measure(
            `dental_${operationName}`,
            `dental_${operationName}_start`,
            `dental_${operationName}_end`
          );
        }
      },

      // Obtener métricas de performance
      getMetrics: () => {
        if (
          typeof performance === "undefined" ||
          !performance.getEntriesByType
        ) {
          return [];
        }

        return performance
          .getEntriesByType("measure")
          .filter((entry) => entry.name.startsWith("dental_"))
          .map((entry) => ({
            operation: entry.name.replace("dental_", ""),
            duration: Math.round(entry.duration * 100) / 100,
            timestamp: entry.startTime,
          }));
      },

      // Limpiar métricas
      clearMetrics: () => {
        if (typeof performance !== "undefined" && performance.clearMeasures) {
          const measures = performance.getEntriesByType("measure");
          measures.forEach((measure) => {
            if (measure.name.startsWith("dental_")) {
              performance.clearMeasures(measure.name);
            }
          });
        }
      },
    }),
    []
  );

  return {
    // Cache operations
    setCache,
    getCache,
    clearCache,

    // Timing operations
    debounce,
    throttle,

    // Memoization
    memoizeExpensive,

    // Dental-specific optimizations
    optimizeDentalData,

    // Performance utilities
    performanceUtils,
  };
}
